/**
 * @fileoverview Static decorative hero backdrop for `/discover` (WEB-775).
 *
 * Design source: `design_handoff_discovery_v2/renderings/discover-v2.html`
 * (`.dv-topart` / `.dv-topart__svg`). Three hardcoded groups — a faint
 * coordinate grid, a latitude tick rail, and eight synthetic diamond
 * “hoods.” No API fetch, no GeoJSON, no data props. The shapes sit in the
 * right ~2/3 of a 1440-wide viewBox by their own coordinates; they are
 * **not** Charlotte geography.
 *
 * Shaun Boehringer, 2026-08-17: this supersedes DPL-04 for the default
 * `/discover` header only. Real-geometry rendering stays in
 * {@link DiscoverHeroStripArt} for future surfaces that need it.
 *
 * Strokes/fills use the themed `--accent` role (WEB-650 / DPL-40) so light
 * mode can retint the decoration. Dark `--accent` is the handoff teal.
 *
 * Three properties are load-bearing:
 * - **Always on.** Renders unconditionally — loading / error / empty
 *   footprint state cannot hide it because this module never reads that
 *   state.
 * - **Decorative.** `aria-hidden` on the layer; `focusable="false"` on
 *   the SVG. It never enters the accessibility tree.
 * - **Zero layout influence.** Absolutely positioned full-bleed, so the
 *   hero's measured height is identical whether the layer paints or not
 *   (CLS). Hidden at ≤720 (`hidden min-[721px]:block`) to match DPL-29.
 */

import * as React from 'react';

import { cn } from '@/components/atoms/primitives/utils';

export interface DiscoverHeroStripBackdropProps {
  readonly className?: string;
}

/**
 * Masked left→right so the headline column keeps full contrast. Same ramp
 * as {@link DiscoverHeroStripArt} — reused on the full-width layer (WEB-775)
 * rather than invented again. Subtlety also comes from the low opacities
 * on the grid / lat / hood strokes.
 */
const MASK_IMAGE =
  'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.28) 30%, rgba(0,0,0,0.72) 62%, black 100%)';

/** 31 vertical lines, one every 48px across the 1440 viewBox. */
export const BACKDROP_GRID_VERTICAL_PATHS: readonly string[] = Array.from(
  { length: 31 },
  (_, i) => `M${i * 48} 0V220`,
);

/** 5 horizontal lines at y = 4, 52, 100, 148, 196. */
export const BACKDROP_GRID_HORIZONTAL_PATHS: readonly string[] = [4, 52, 100, 148, 196].map(
  (y) => `M0 ${y}H1440`,
);

/** One long parallel at y=110. */
export const BACKDROP_LAT_LINE_PATH = 'M0 110H1440';

/** 15 tick marks every 96px along the latitude rail. */
export const BACKDROP_LAT_TICK_PATHS: readonly string[] = Array.from(
  { length: 15 },
  (_, i) => `M${i * 96} 106v8`,
);

/**
 * Eight evenly-spaced synthetic diamonds from the design handoff.
 * Coordinates are decorative — they are not neighborhood footprints.
 */
export const BACKDROP_HOOD_PATHS: readonly string[] = [
  'M469.7 -9.1 L538.0 82.9 L428.7 133.1 L374.0 41.1 L469.7 -9.1 Z',
  'M548.8 150.0 L622.3 80.0 L663.2 120.0 L589.7 200.0 L548.8 150.0 Z',
  'M751.6 133.5 L682.0 80.3 L795.1 -15.5 L856.0 48.4 L751.6 133.5 Z',
  'M860.0 122.0 L900.0 94.0 L940.0 185.0 L888.6 206.0 L860.0 122.0 Z',
  'M978.6 41.2 L1046.2 -14.0 L1121.4 124.0 L1038.7 170.0 L978.6 41.2 Z',
  'M1134.0 130.2 L1198.9 90.4 L1272.0 179.8 L1174.6 219.6 L1134.0 130.2 Z',
  'M1321.4 -10.0 L1379.1 13.6 L1350.2 96.0 L1282.9 60.7 L1321.4 -10.0 Z',
  'M1444.4 64.0 L1519.0 113.8 L1424.0 230.0 L1363.0 163.6 L1444.4 64.0 Z',
];

export function DiscoverHeroStripBackdrop({
  className,
}: DiscoverHeroStripBackdropProps): React.ReactElement {
  return (
    <div
      aria-hidden
      data-testid="discover-hero-strip-backdrop"
      className={cn(
        // `-z-10` sits the art behind the hero's copy. The section owns the
        // stacking context, so this stays above the section's own gradient.
        'pointer-events-none absolute inset-0 -z-10 overflow-hidden',
        // DPL-29 reclaims the ≤720 fold — the art is the first thing to go.
        'hidden min-[721px]:block',
        className,
      )}
      style={{ maskImage: MASK_IMAGE, WebkitMaskImage: MASK_IMAGE }}
    >
      <svg
        className="absolute inset-0 h-full w-full"
        viewBox="0 0 1440 220"
        preserveAspectRatio="xMaxYMid slice"
        aria-hidden
        focusable="false"
        fill="none"
      >
        <g
          data-testid="discover-hero-strip-backdrop-grid"
          stroke="var(--fg-1)"
          strokeWidth={1}
          opacity={0.05}
        >
          {BACKDROP_GRID_VERTICAL_PATHS.map((d) => (
            <path key={d} d={d} />
          ))}
          {BACKDROP_GRID_HORIZONTAL_PATHS.map((d) => (
            <path key={d} d={d} />
          ))}
        </g>
        <g
          data-testid="discover-hero-strip-backdrop-lat"
          stroke="var(--accent)"
          strokeWidth={1}
          opacity={0.16}
        >
          <path d={BACKDROP_LAT_LINE_PATH} />
          {BACKDROP_LAT_TICK_PATHS.map((d) => (
            <path key={d} d={d} />
          ))}
        </g>
        <g
          data-testid="discover-hero-strip-backdrop-hoods"
          stroke="var(--accent)"
          strokeWidth={1.25}
          strokeLinejoin="round"
          opacity={0.38}
          fill="var(--accent)"
          fillOpacity={0.06}
        >
          {BACKDROP_HOOD_PATHS.map((d) => (
            <path key={d} d={d} />
          ))}
        </g>
      </svg>
    </div>
  );
}
